import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  ShieldCheck, Key, Cube, Users, ClipboardText, Warning,
  ArrowsLeftRight, Brain, Ticket, PaperPlaneTilt, GearSix,
  Lightning, Plus, ArrowRight
} from '@phosphor-icons/react';

const EMPTY_STATES = {
  roles: {
    icon: ShieldCheck,
    title: 'No Roles Configured',
    description: 'Roles bundle entitlements together so users can request access in one step. Create your first role to get started.',
    action: 'Create Role',
    testId: 'empty-roles',
  },
  entitlements: {
    icon: Key,
    title: 'No Entitlements Found',
    description: 'Entitlements are individual permissions (e.g., Azure AD groups). Aggregate from Azure AD or create them manually.',
    action: 'Create Entitlement',
    testId: 'empty-entitlements',
  },
  applications: {
    icon: Cube,
    title: 'No Applications Registered',
    description: 'Applications represent the systems your organization uses. Register apps to manage their accounts and entitlements.',
    action: 'Add Application',
    testId: 'empty-applications',
  },
  identities: {
    icon: Users,
    title: 'No Identities Found',
    description: 'Identities represent the people in your organization. Aggregate from Azure AD or create them manually.',
    action: 'Create Identity',
    testId: 'empty-identities',
  },
  certifications: {
    icon: ClipboardText,
    title: 'No Certification Campaigns',
    description: 'Certification campaigns let managers periodically review who has access to what — keeping your organization secure and compliant.',
    action: 'Create Campaign',
    testId: 'empty-certifications',
  },
  sod: {
    icon: Warning,
    title: 'No SoD Policies Defined',
    description: 'Separation of Duties policies prevent risky access combinations — like one person having both "Create Invoices" and "Approve Payments".',
    action: 'Create Policy',
    testId: 'empty-sod',
  },
  lifecycle: {
    icon: ArrowsLeftRight,
    title: 'No Automation Rules',
    description: 'Lifecycle rules automatically assign or revoke roles when employees join, change teams, or leave your organization.',
    action: 'Create Rule',
    testId: 'empty-lifecycle',
  },
  lifecycle_events: {
    icon: ArrowsLeftRight,
    title: 'No Lifecycle Events Recorded',
    description: 'Events will appear here when lifecycle automations are triggered — during Azure AD aggregation or manual triggers.',
    testId: 'empty-lifecycle-events',
  },
  provisioning: {
    icon: Ticket,
    title: 'No Provisioning Tickets',
    description: 'Provisioning tickets are created when access changes need manual action — for systems not connected to Azure AD.',
    testId: 'empty-provisioning',
  },
  access_requests: {
    icon: PaperPlaneTilt,
    title: 'No Access Requests Yet',
    description: 'Access requests will appear here when users request roles or entitlements through the portal.',
    testId: 'empty-access-requests',
  },
  role_assignments: {
    icon: ShieldCheck,
    title: 'No Role Assignments',
    description: 'Role assignments are created when users are granted access to roles — through requests, auto-assignment, or manual assignment.',
    testId: 'empty-role-assignments',
  },
  sources: {
    icon: GearSix,
    title: 'No Data Sources Configured',
    description: 'Data sources connect your IGA platform to identity providers like Azure AD and manual application systems.',
    action: 'Add Source',
    testId: 'empty-sources',
  },
  jit_requests: {
    icon: Lightning,
    title: 'No JIT Requests Found',
    description: 'Just-in-Time requests will appear here as users request temporary privileged access.',
    testId: 'empty-jit-requests',
  },
  audit: {
    icon: ClipboardText,
    title: 'No Audit Logs Found',
    description: 'Audit logs track every action taken in the platform — role changes, approvals, provisioning, and more.',
    testId: 'empty-audit',
  },
};

export default function EmptyState({ type, onAction, className = '' }) {
  const navigate = useNavigate();
  const config = EMPTY_STATES[type];
  if (!config) return null;

  const Icon = config.icon;

  return (
    <div className={`flex flex-col items-center justify-center py-14 px-6 ${className}`} data-testid={config.testId}>
      <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
        <Icon size={28} weight="duotone" className="text-navy-400" />
      </div>
      <h3 className="text-base font-semibold text-navy-900 mb-1">{config.title}</h3>
      <p className="text-sm text-navy-500 text-center max-w-md mb-5 leading-relaxed">{config.description}</p>
      {config.action && onAction && (
        <Button
          onClick={onAction}
          className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm shadow-sm"
          data-testid={`${config.testId}-action`}
        >
          <Plus size={14} className="mr-1.5" />
          {config.action}
        </Button>
      )}
    </div>
  );
}

export { EMPTY_STATES };
